import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardContent, Container, Typography, Grid, IconButton, Tooltip, FormControl, InputLabel, Select, MenuItem, Chip, OutlinedInput } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { getRequest, postRequest } from "../../Fetch/request";
import Loader from "../Loader/Loader";
import DSA_TOPICS from '../../Utils/DSATopics';
import { useAuth } from '../../hooks/useAuth';

const SessionDetails = () => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [creatingSession, setCreatingSession] = useState(false);

    const [selectedTopics, setSelectedTopics] = useState([]);

    // Display names for dropdown - generated from DSA_TOPICS
    const dsaTopics = Object.keys(DSA_TOPICS).map(key => {
        return key.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
    });

    // Helper function to map display names to backend values
    const mapTopicToBackendValue = (displayName) => {
        // Convert display name back to key format, then get backend value
        const topicKey = displayName.toUpperCase().replace(/\s+/g, '_');
        return DSA_TOPICS[topicKey];
    };

    useEffect(() => {
        fetchUserSessions();
    }, []);

    const fetchUserSessions = async () => {
        try {
            setLoading(true);
            
            const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
            
            const response = await getRequest(`${process.env.REACT_APP_URL}/session/user-sessions?userId=${userId}`);
            
            if (response && (response.status === "200" || response.status === 200)) {
                setSessions(response.response?.sessions || []);
            } else {
                setError("Failed to fetch sessions");
            }
        } catch (error) {
            console.error('Error fetching sessions:', error);
            setError("Failed to fetch sessions");
        } finally {
            setLoading(false);
        }
    };

    const handleContinueSession = (sessionId) => {
        navigate(`/session-problems?sessionId=${sessionId}`);
    };

    const handleCreateNewSession = async () => {
        if (!isAuthenticated) {
            alert('Please login to create DSA sessions');
            return;
        }

        try {
            setCreatingSession(true);
            
            const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';

            // Map display names to backend values
            const mappedTopicIds = selectedTopics.map(topic => mapTopicToBackendValue(topic));

            const requestBody = {
                userId: userId,
                type: 'DSA', // Default session type
                topicIds: mappedTopicIds // Pass mapped backend values (empty if none selected)
            };

            const response = await postRequest(`${process.env.REACT_APP_URL}/session/create-session`, requestBody);

            if (response && (response.status === "200" || response.status === 200 || response.status === "201" || response.status === 201)) {
                // Show simple browser alert
                alert("Request received! You will get session problems in few seconds.");
                // Refresh the sessions list to show the new session
                fetchUserSessions();
            } else {
                setError("Failed to create session");
            }
        } catch (error) {
            console.error('Error creating session:', error);
            setError("Failed to create session");
        } finally {
            setCreatingSession(false);
        }
    };



    const handleTopicChange = (event) => {
        const value = event.target.value;
        const newTopics = typeof value === 'string' ? value.split(',') : value;
        
        // Limit to maximum 5 topics
        if (newTopics.length <= 5) {
            setSelectedTopics(newTopics);
        }
    };



    if (loading) {
        return (
            <Container maxWidth="xl" sx={{ marginTop: '20px', padding: '20px' }}>
                <Loader />
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="xl" sx={{ marginTop: '20px', padding: '20px' }}>
                <Typography variant="h6" color="error" textAlign="center" sx={{ mb: 2 }}>
                    {error}
                </Typography>
                <Box sx={{ textAlign: 'center' }}>
                    <Button 
                        variant="contained" 
                        onClick={fetchUserSessions}
                        sx={{ 
                            backgroundColor: '#008080',
                            color: '#fff',
                            marginRight: 2,
                            '&:hover': { backgroundColor: '#006666' }
                        }}
                    >
                        Refresh Sessions
                    </Button>
                    <Button 
                        variant="outlined" 
                        onClick={() => navigate('/')}
                        sx={{ 
                            borderColor: '#008080',
                            color: '#008080',
                            '&:hover': { 
                                backgroundColor: '#008080',
                                color: '#fff'
                            }
                        }}
                    >
                        Back to Dashboard
                    </Button>
                </Box>
            </Container>
        );
    }

    const renderSessions = () => {
        if (!sessions || sessions.length === 0) {
            return (
                <Grid item xs={12} sm={6} md={4} lg={3}>
                    <Card
                        sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            textAlign: 'center',
                            padding: '40px 20px',
                            borderRadius: '15px',
                            border: '2px dashed #008080',
                            backgroundColor: '#f8f9fa',
                            cursor: 'pointer',
                            transition: 'transform 0.3s ease-in-out',
                            '&:hover': {
                                transform: 'scale(1.05)',
                                backgroundColor: '#e8f5e8',
                            },
                        }}
                        onClick={handleCreateNewSession}
                        disabled={creatingSession}
                    >
                        <Typography
                            variant="h4"
                            sx={{
                                color: '#008080',
                                marginBottom: '10px',
                                fontSize: '3rem',
                            }}
                        >
                            +
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                color: creatingSession ? '#ccc' : '#008080',
                                fontWeight: 'bold',
                                marginBottom: '10px',
                            }}
                        >
                            {creatingSession ? 'Creating Session...' : 'Create New Session'}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: creatingSession ? '#ccc' : '#666',
                            }}
                        >
                            {creatingSession ? 'Creating session... Please wait a few moments' : 'Start your first practice session'}
                        </Typography>
                    </Card>
                </Grid>
            );
        }

        return sessions.map((session) => (
            <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                key={session.sessionId}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '10px',
                }}
            >
                <Card
                    sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        textAlign: 'center',
                        padding: '20px',
                        borderRadius: '15px',
                        position: 'relative',
                        transition: 'transform 0.3s ease-in-out',
                        backgroundColor: '#fff',
                        color: '#333',
                        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                        cursor: 'pointer',
                        '&:hover': {
                            transform: 'scale(1.05)',
                            backgroundColor: '#f9f9f9',
                        },
                    }}
                    onClick={() => handleContinueSession(session.sessionId)}
                >
                    {/* Centered Topics Covered */}
                    <CardContent
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flexGrow: 1,
                            marginTop: '20px',
                            gap: '10px',
                            textAlign: 'center'
                        }}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                textAlign: 'center',
                                fontWeight: 'bold',
                                width: '100%',
                            }}
                        >
                            {session.problemIds ? 
                                session.problemIds.map(p => p.topicId).filter((v, i, a) => a.indexOf(v) === i).join(' | ') 
                                : 'Session Topics'
                            }
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                textAlign: 'center',
                                color: '#666',
                                fontSize: '12px'
                            }}
                        >
                            Created: {new Date(session.createdAt).toLocaleDateString()}
                        </Typography>
                    </CardContent>
                </Card>
            </Grid>
        ));
    };

    return (
        <Container
            maxWidth="xl"
            sx={{
            }}
        >
            {/* Information Banner */}
            <Box sx={{ px: 2 }}>
                <Box sx={{ pt: 0, pb: 2, px: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <InfoIcon sx={{ color: '#666', fontSize: '20px' }} />
                        <Typography variant="body2" sx={{
                            color: '#666',
                            fontSize: '14px'
                        }}>
                            Create practice sessions with 4-5 problems based on your past performance. Select topics and get personalized questions tailored to your performance history.
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Topic Selection and Create Button */}
            <Box sx={{ px: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
                    <FormControl sx={{ flexGrow: 1 }} size="small">
                        <InputLabel id="topic-select-label">Select Topics (Optional - Max 5)</InputLabel>
                        <Select
                            labelId="topic-select-label"
                            id="topic-select"
                            multiple
                            value={selectedTopics}
                            onChange={handleTopicChange}
                            input={<OutlinedInput size="small" label="Select Topics (Optional - Max 5)" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25, maxHeight: '32px', overflow: 'hidden' }}>
                                    {selected.slice(0, 3).map((value) => (
                                        <Chip 
                                            key={value} 
                                            label={value} 
                                            size="small"
                                            sx={{
                                                backgroundColor: '#008080',
                                                color: 'white',
                                                fontSize: '11px',
                                                height: '20px'
                                            }}
                                        />
                                    ))}
                                    {selected.length > 3 && (
                                        <Chip 
                                            label={`+${selected.length - 3} more`}
                                            size="small"
                                            sx={{
                                                backgroundColor: '#666',
                                                color: 'white',
                                                fontSize: '11px',
                                                height: '20px'
                                            }}
                                        />
                                    )}
                                </Box>
                            )}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 200
                                    }
                                }
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: '#e0e0e0',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#008080',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#008080',
                                    },
                                },
                            }}
                        >
                            {dsaTopics.map((topic) => (
                                <MenuItem 
                                    key={topic} 
                                    value={topic}
                                    disabled={selectedTopics.length >= 5 && !selectedTopics.includes(topic)}
                                >
                                    {topic}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        onClick={handleCreateNewSession}
                        disabled={creatingSession}
                        sx={{
                            backgroundColor: '#fff',
                            color: '#008080',
                            borderRadius: '20px',
                            padding: '10px 24px',
                            fontWeight: 'bold',
                            border: '2px solid #008080',
                            height: '40px',
                            minWidth: '120px',
                            '&:hover': {
                                backgroundColor: '#008080',
                                color: '#fff',
                            },
                            '&:disabled': {
                                backgroundColor: '#ccc',
                                color: '#666',
                                border: '2px solid #ccc',
                            },
                        }}
                    >
                        {creatingSession ? 'Creating...' : '+ Create'}
                    </Button>
                </Box>
                {selectedTopics.length > 0 && (
                    <Typography variant="caption" sx={{ 
                        color: selectedTopics.length >= 5 ? '#f57c00' : '#666', 
                        fontSize: '11px', 
                        mt: 0.5,
                        display: 'block'
                    }}>
                        {selectedTopics.length}/5 topics selected
                        {selectedTopics.length >= 5 && " (Maximum reached)"}
                    </Typography>
                )}
            </Box>
            <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
                {renderSessions()}
            </Grid>
        </Container>
    );
};

export default SessionDetails;
